import React, { createContext, useContext, useRef } from 'react';
import { Star } from 'lucide-react';
import CardImage from './CardImage';
import { useCardPeek } from './useCardPeek';
import './CardTile.css';

// Reicht das Ergebnis von `useCardPeek` und den Bild-Ref von der Kachel an `CardTile.Image`;
// außerhalb einer Kachel (Tests, Showcase) bleibt CardTile.Image ohne Hook.
const CardTileContext = createContext({ peek: undefined, imageRef: null });

const TOUCH = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;

/**
 * Baustein 5 — Card-Tile.
 * Karten-Kachel mit Kartenbild + Info-Panel. Verwendung:
 *   - Card-Grid (Sammlung-typisch, große Tiles, verschiedene Karten)
 *   - Variant-Grid (Print-Picker, kleinere Tiles, gleiche Karte verschiedene Prints)
 * Die Größe steuert das umschließende `<CardGrid>` über `variant`.
 *
 * Das Anheben bei Hover hängt an `@media (hover: hover)`, also am Eingabegerät und nicht an
 * der Fensterbreite: auf Touch bliebe `:hover` nach dem Tippen kleben und die Kachel sähe
 * aus wie ausgewählt. In einem schmalen Desktop-Fenster bleibt das Anheben erhalten.
 *
 * Komposition:
 *   <CardTile selected onClick={...}>
 *     <CardTile.Image src="..." />
 *     <CardTile.Info>
 *       <CardTile.Row>
 *         <CardTile.Set>DSK</CardTile.Set>
 *         <CardTile.Qty>9×</CardTile.Qty>
 *       </CardTile.Row>
 *       <CardTile.Source variant="binder">Jump Scare!</CardTile.Source>
 *     </CardTile.Info>
 *   </CardTile>
 *
 * Props (CardTile):
 *   selected   — boolean, Accent-Border + Box-Shadow
 *   onClick    — Click-Handler; rendert ein unsichtbares, über die ganze
 *                Kachel gestrecktes <button> als Geschwister des Inhalts.
 *                Die Kachel selbst ist ein <div>: auf dem Kartenbild liegen
 *                eigene Bedienelemente (Flip-Button, Overlays), und ein
 *                umschließender Button wäre ein Button im Button
 *                (ungültiges HTML, kaputte Tastatur-/Screenreader-Bedienung).
 *   clickLabel — Name des Klick-Ziels für Screenreader (z.B. der Kartenname);
 *                Pflicht zusammen mit onClick, das gestreckte <button> hat
 *                sonst keinen zugänglichen Namen.
 *   className  — zusätzliche Klassen am Wrapper
 *   scryfallId — macht die Kachel per HTML5-Drag ziehbar (Nutzlast: Scryfall-Adresse); weglassen,
 *                wo dnd-kit die Kachel zieht.
 *
 * Querformat-Karten: die Kachel hält `ui/useCardPeek` an ihrem Wrapper (die Zeiger-Ereignisse
 * treffen das gestreckte Klick-Ziel, nicht das Bild) und reicht `peek` an `CardTile.Image`,
 * die das Bild im Modus `landscape="peek"` rendert — ruht eine Maus auf einer Querformat-Karte
 * (`orientation` aus `imageOrientation` des Umschlags), legt `CardImage` ein gedrehtes Bild
 * darüber. Umdreh- und Dreh-Knopf (`card-image__control`) bleiben unter dem Klick-Ziel
 * klickbar (CardTile.css).
 */
export default function CardTile({ selected = false, onClick, clickLabel, className = '', scryfallId, children }) {
  const imageRef = useRef(null);
  const { peek, handlers } = useCardPeek({ imageRef });
  const classes = ['card-tile'];
  if (selected) classes.push('card-tile--selected');
  if (className) classes.push(className);
  // Browser-eigenes Ziehen mit der Scryfall-Adresse als Nutzlast: der Deck-Editor eines anderen
  // Tabs (und fremde Seiten, die Scryfall-Links annehmen) nehmen die Karte so an. Auf Touch
  // bleibt es aus, es störte Scrollen und Langdrücken. Hosts mit eigenem dnd-kit-Zug übergeben
  // keine scryfallId; beide Mechanismen auf einem Element vertragen sich nicht.
  const ziehbar = Boolean(scryfallId) && !TOUCH;
  const onDragStart = ziehbar ? (e) => {
    const url = `https://scryfall.com/card/${scryfallId}`;
    e.dataTransfer.setData('text/uri-list', url);
    e.dataTransfer.setData('text/plain', url);
    e.dataTransfer.effectAllowed = 'copy';
  } : undefined;
  return (
    <div className={classes.join(' ')} draggable={ziehbar || undefined} onDragStart={onDragStart} {...handlers}>
      {onClick && (
        <button
          type="button"
          className="card-tile__click"
          aria-label={clickLabel}
          onClick={onClick}
        />
      )}
      <CardTileContext.Provider value={{ peek, imageRef }}>
        {children}
      </CardTileContext.Provider>
    </div>
  );
}

/**
 * Image-Slot — dünne Hülle um das Master-Primitiv `ui/CardImage`.
 * Flip-Button, 3D-Rotation, onError-Platzhalter und der feste Ecken-Radius
 * leben dort — Tiles erben sie automatisch. `children` ist der Overlay-Slot
 * von `ui/CardImage` und wird unverändert durchgereicht. `orientation` ist
 * `imageOrientation` aus dem Umschlag; das Hüll-`<div>` trägt den Bild-Ref für
 * `useCardPeek` (CardImage reicht keinen Ref durch).
 */
CardTile.Image = function CardTileImage({ src, backSrc, alt = '', orientation, children }) {
  const { peek, imageRef } = useContext(CardTileContext);
  return (
    <div className="card-tile__image" ref={imageRef}>
      <CardImage src={src} backSrc={backSrc} alt={alt} landscape="peek" peek={peek} orientation={orientation}>
        {children}
      </CardImage>
    </div>
  );
};

/** Info-Panel unter dem Bild. Enthält ein oder mehrere `<CardTile.Row>`. */
CardTile.Info = function CardTileInfo({ children }) {
  return <div className="card-tile__info">{children}</div>;
};

/** Zwei-Spalten-Zeile (links / rechts) innerhalb des Info-Panels. */
CardTile.Row = function CardTileRow({ children }) {
  return <div className="card-tile__row">{children}</div>;
};

/** Karten-Name (primärer Text). */
CardTile.Name = function CardTileName({ children }) {
  return <span className="card-tile__name">{children}</span>;
};

/** Set-Code (z.B. DSK, MKM). */
CardTile.Set = function CardTileSet({ children }) {
  return <span className="card-tile__set">{children}</span>;
};

/** Menge (z.B. 9×). */
CardTile.Qty = function CardTileQty({ children }) {
  return <span className="card-tile__qty">{children}</span>;
};

/** Meta-Info (Condition, Sprache, …) — sekundärer Text. */
CardTile.Meta = function CardTileMeta({ children }) {
  return <span className="card-tile__meta">{children}</span>;
};

/** Preis (rechtsbündige Spalte). */
CardTile.Price = function CardTilePrice({ children }) {
  return <span className="card-tile__price">{children}</span>;
};

/** Gruppe rechts (Qty + Foil + Price zusammen). */
CardTile.PriceGroup = function CardTilePriceGroup({ children }) {
  return <span className="card-tile__price-group">{children}</span>;
};

/**
 * Source — wo die Karte „lebt".
 * variant='deck' (Accent-Farbe) oder 'binder' (muted).
 */
CardTile.Source = function CardTileSource({ variant = 'binder', children }) {
  return (
    <div className={`card-tile__source card-tile__source--${variant}`}>
      {children}
    </div>
  );
};

/** Foil-Icon (Stern mit Foil-Farbe). */
CardTile.Foil = function CardTileFoil() {
  return <Star size={12} fill="currentColor" className="card-tile__foil" aria-label="Foil" />;
};
